'use client'
import { PaymentType } from '@repo/shared/components'
import {
  type MessagesKeys,
  useLanguage,
  useTranslate,
} from '@repo/shared/hooks'
import {
  AspectRatio,
  Badge,
  Card,
  Col,
  DeltaBadge,
  Icon,
  Row,
  Timer,
  Typography,
} from '@repo/ui/components'
import Image from 'next/image'
import { Link } from '@repo/shared/i18n/navigation'
import { globalConfig } from '@repo/shared/utils'
import type { ProjectsCardItemProps } from './projects.card-item.types'
import { cn } from '@repo/ui/lib'
import { ProjectsCardItemChart } from './projects.card-item.chart'
import { useIsDesktop } from '@repo/ui/hooks'

export const ProjectsCardItem = ({
  project,
  viewType = 'list',
}: ProjectsCardItemProps) => {
  const { t } = useTranslate()

  const statusBadgeConfigs: Record<
    string,
    {
      color: 'positive' | 'alert' | 'disabled'
      variant: 'solid' | 'liquid'
    }
  > = {
    coming_soon: {
      color: 'positive',
      variant: 'solid',
    },
    suspended: {
      color: 'alert',
      variant: 'liquid',
    },
    delisting: {
      color: 'disabled',
      variant: 'liquid',
    },
    off_market: {
      color: 'disabled',
      variant: 'liquid',
    },
  }

  const imgBig = project.defaultPhotoID
    ? project?.photos?.find((x) => x.id === project.defaultPhotoID)?.bigURL
    : project?.photos?.[0]?.bigURL
  const imgBigUrl = imgBig
    ? `${globalConfig.baseImageUrl}${imgBig}`
    : '/images/placeholder.webp'

  const logoUrl = project?.logoURL
    ? `${globalConfig.baseImageUrl}${project?.logoURL}`
    : '/images/placeholder.webp'

  const language = useLanguage()
  const isDesktop = useIsDesktop()

  if (viewType === 'grid') {
    return (
      <Link
        href={`/rwa/${project.token}`}
        className='block w-full xs:max-w-[280px]'
        prefetch='auto'
      >
        <Card className='flex flex-col p-xs !gap-0' includeShadow>
          <div
            className={cn(
              'relative w-full min-h-30 rounded-2xs overflow-hidden',
              project.tradeStatus === 'suspended' ||
                project.tradeStatus === 'delisting'
                ? 'grayscale'
                : '',
            )}
          >
            <Image
              src={imgBigUrl}
              alt='Photo of property'
              fill
              className='object-cover w-full h-full'
              sizes='(max-width: 768px) 100vw, 320px'
            />
          </div>
          <Col className='gap-md p-xs flex-1'>
            {project.displayName && (
              <Row className='gap-2xs flex-wrap min-h-[40px]'>
                <Typography
                  size='sm'
                  color='text-neutral-surface-100'
                  className='flex-1 min-w-0'
                >
                  {project.displayName}
                </Typography>
              </Row>
            )}

            <Col className='gap-xs'>
              <Row className='gap-xs'>
                <Row className='gap-xs items-center'>
                  {project.token && (
                    <Typography
                      size='xs'
                      color='text-neutral-surface-60'
                      className='font-[AzarMehr]'
                    >
                      {project.token}
                    </Typography>
                  )}
                </Row>
                <div className='w-3xl'>
                  <AspectRatio className=' aspect-1/1'>
                    <Image
                      src={logoUrl}
                      alt='Photo of property'
                      fill
                      sizes='(max-width: 768px) 100vw, 32px'
                      className='h-full w-full  rounded-2xs object-cover'
                      loading='lazy'
                    />
                  </AspectRatio>
                </div>
              </Row>

              <Row className='gap-sm flex-wrap'>
                <Row className='gap-2xs items-center w-fit'>
                  {project.location?.province?.name && (
                    <Typography size='xs' color='text-neutral-surface-60'>
                      {project.location.province.name}
                    </Typography>
                  )}
                  {project.location?.district?.name && (
                    <Typography size='xs' color='text-neutral-surface-60'>
                      {project.location.district.name}
                    </Typography>
                  )}
                </Row>
                {project.type && (
                  <Typography size='xs' color='text-neutral-surface-60'>
                    {t(`filters_usage_${project.type}` as MessagesKeys)}
                  </Typography>
                )}
              </Row>
              <Row className='gap-sm flex-wrap'>
                <Row className='gap-3xs items-center w-fit'>
                  <Icon
                    name='Apartment'
                    size='xs'
                    color='fill-neutral-surface-60'
                  />
                  <Typography size='xs' className='text-neutral-surface-60'>
                    {t(`common_${project.state}` as MessagesKeys)}
                  </Typography>
                </Row>
              </Row>
            </Col>

            {project.tradeStatus !== 'coming_soon' && (
              <Row className='justify-between items-end gap-md mt-auto'>
                <ProjectsCardItemChart project={project} />
                <Col className='gap-2xs items-end'>
                  {project.tradeStatus && project.tradeStatus !== 'active' ? (
                    <Badge
                      size='sm'
                      variant={statusBadgeConfigs[project.tradeStatus]?.variant}
                      color={statusBadgeConfigs[project.tradeStatus]?.color}
                    >
                      {t(
                        `common_project_trade_status_${project.tradeStatus}` as MessagesKeys,
                      )}
                    </Badge>
                  ) : (
                    <>
                      <DeltaBadge
                        variant='ghost'
                        size='sm'
                        value={project.price?.priceRange24h?.deltaPercent || 0}
                      />
                      <PaymentType
                        value={+(project.price?.priceRange24h?.last || 0)}
                        size='2xs'
                        numberSize='sm'
                        isBold={true}
                      />
                    </>
                  )}
                </Col>
              </Row>
            )}
            {project.tradeStatus === 'coming_soon' && (
              <Col className='justify-between items-center gap-md mt-auto'>
                <Row className='gap-2xs items-center justify-between'>
                  <Row className='gap-2xs items-center w-fit'>
                    <Icon
                      name='Clock'
                      size='xs'
                      color='fill-neutral-surface-60'
                    />
                    <Badge
                      size='sm'
                      variant={statusBadgeConfigs[project.tradeStatus]?.variant}
                      color={statusBadgeConfigs[project.tradeStatus]?.color}
                    >
                      {t(
                        `common_project_trade_status_${project.tradeStatus}` as MessagesKeys,
                      )}
                    </Badge>
                  </Row>

                  <Typography size='xs' color='text-neutral-surface-60'>
                    {project.market?.listingDate &&
                    project.market?.listingDate > new Date().getTime() ? (
                      <Timer
                        targetDate={new Date(project.market?.listingDate || 0)}
                        onComplete={() => {}}
                        language={language === 'fa' ? 'fa' : 'en'}
                      />
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Row>

                <Row className='gap-2xs items-center justify-between'>
                  <Row className='gap-2xs items-center w-fit'>
                    <Icon
                      name='Coin'
                      size='xs'
                      color='fill-neutral-surface-60'
                    />
                    <Typography size='xs' color='text-neutral-surface-60'>
                      {t('projectsPage_base_price')}
                    </Typography>
                  </Row>

                  <PaymentType
                    value={+(project.price?.priceRange24h?.last || 0)}
                    size='2xs'
                    numberSize='sm'
                    isBold={true}
                  />
                </Row>
              </Col>
            )}
          </Col>
        </Card>
      </Link>
    )
  }

  return (
    <Link
      className='p-xs gap-md   flex flex-row  items-center w-full border-b border-neutral-surface-5  last:border-none'
      href={`/rwa/${project.token}`}
      prefetch='auto'
    >
      <div className='w-3xl'>
        <AspectRatio className=' aspect-1/1'>
          <Image
            src={logoUrl}
            alt='Photo of property'
            fill
            sizes='(max-width: 768px) 100vw, 32px'
            className='h-full w-full  rounded-2xs object-cover'
            loading='lazy'
          />
        </AspectRatio>
      </div>

      <div className='gap-xs flex  md:items-center md:justify-between w-full flex-col'>
        {project.displayName && (
          <Row className='gap-2xs'>
            <Typography
              size='md'
              color='text-neutral-surface-100'
              className='text-sm md:text-md'
            >
              {project.displayName}{' '}
              {project.tradeStatus && project.tradeStatus !== 'active' && (
                <Badge
                  size='sm'
                  variant={statusBadgeConfigs[project.tradeStatus]?.variant}
                  color={statusBadgeConfigs[project.tradeStatus]?.color}
                >
                  {t(
                    `common_project_trade_status_${project.tradeStatus}` as MessagesKeys,
                  )}
                </Badge>
              )}
            </Typography>
          </Row>
        )}
        <Row>
          <Col className='gap-sm'>
            <Row className='gap-xs'>
              {project.token && (
                <Typography
                  size='xs'
                  color='text-neutral-surface-60'
                  className='font-[AzarMehr]'
                >
                  {project.token}
                </Typography>
              )}
            </Row>
          </Col>
          <Col className='justify-end gap-2xs md:gap-2xl w-fit  items-end md:items-center flex-col md:flex-row '>
            {isDesktop && <ProjectsCardItemChart project={project} />}
            <DeltaBadge
              variant='ghost'
              size='sm'
              value={project.price?.priceRange24h?.deltaPercent || 0}
            />
            <Row className='gap-2xs w-fit'>
              {/* <Typography size='sm' color='text-neutral-surface-60'>
                {t('projectsPage_price_per_meter')}
              </Typography> */}
              <PaymentType
                value={+(project.price?.priceRange24h?.last || 0)}
                size='2xs'
                numberSize='sm'
                isBold={true}
              />
            </Row>
          </Col>
        </Row>
      </div>
    </Link>
  )
}
